import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2 } from "lucide-react";
import { PageContainer } from "@/layout/PageContainer";
import { PageHeader } from "@/ui/PageHeader";
import { KPICard } from "@/ui/KPICard";
import { Card } from "@/ui/Card";
import { Badge } from "@/ui/Badge";
import { Avatar } from "@/ui/Avatar";
import { Button } from "@/ui/Button";
import { SegmentedFilter, type FilterOption } from "@/ui/SegmentedFilter";
import { DataTable, type Column } from "@/ui/data-table/DataTable";
import { ConfirmDialog } from "@/ui/modals/ConfirmDialog";
import { Modal, Field, fieldInputClass } from "@/ui/modals/Modal";
import { LoadingState } from "@/ui/feedback/states";
import { ApiFallbackBanner } from "@/ui/feedback/ApiFallbackBanner";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { formatUSD, formatDZD, usdToDzd } from "@/lib/formatters";
import { useLabels } from "@/lib/labels";
import type { AdSpend, Platform, CardSource } from "@/types";
import { useClients } from "@/features/clients/clients.hooks";
import { useAdSpend, useCreateAdSpend, useArchiveAdSpend } from "./adspend.hooks";
import type { AdSpendInput } from "./adspend.adapter";

type PlatformFilter = "all" | Platform;
const FILTERS: FilterOption<PlatformFilter>[] = [
  { value: "all", label: "Toutes plateformes" },
  { value: "META", label: "Meta" },
  { value: "TIKTOK", label: "TikTok" },
  { value: "GOOGLE", label: "Google" },
];

export function AdSpendPage() {
  const { t } = useTranslation();
  const { PLATFORM, CARD_SOURCE } = useLabels();
  const { adSpend, isLoading, isError, refetch } = useAdSpend();
  const createAdSpend = useCreateAdSpend();
  const archiveAdSpend = useArchiveAdSpend();
  const { clients } = useClients();
  const [filter, setFilter] = useState<PlatformFilter>("all");
  const [toDelete, setToDelete] = useState<AdSpend | undefined>();
  const [addOpen, setAddOpen] = useState(false);

  const visible = useMemo(() => (filter === "all" ? adSpend : adSpend.filter((a) => a.platform === filter)), [adSpend, filter]);
  const stats = useMemo(() => {
    const total = adSpend.reduce((s, a) => s + a.amountUsd, 0);
    const agency = adSpend.filter((a) => a.source === "AGENCY_CARD").reduce((s, a) => s + a.amountUsd, 0);
    const client = adSpend.filter((a) => a.source === "CLIENT_CARD").reduce((s, a) => s + a.amountUsd, 0);
    return { total, agency, client };
  }, [adSpend]);

  const onCreate = async (input: AdSpendInput) => {
    try {
      await createAdSpend.mutateAsync(input);
      toast.success("Dépense ajoutée");
      setAddOpen(false);
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec de l'ajout de la dépense"));
    }
  };

  const onArchiveConfirm = async () => {
    if (!toDelete) return;
    try {
      await archiveAdSpend.mutateAsync(toDelete.id);
      toast.success("Dépense archivée");
    } catch (err) {
      toast.error(getErrorMessage(err, "Échec de l'archivage"));
    } finally {
      setToDelete(undefined);
    }
  };

  const columns: Column<AdSpend>[] = [
    { key: "date", header: t("adSpend.columns.date"), cell: (a) => <span className="text-text3">{a.date}</span> },
    { key: "client", header: t("adSpend.columns.client"), cell: (a) => <div className="flex items-center gap-2"><Avatar name={a.clientName} size={22} />{a.clientName}</div> },
    { key: "platform", header: t("adSpend.columns.platform"), cell: (a) => <Badge tone={PLATFORM[a.platform].tone}>{PLATFORM[a.platform].label}</Badge> },
    { key: "source", header: t("adSpend.columns.source"), cell: (a) => <Badge tone={CARD_SOURCE[a.source].tone}>{CARD_SOURCE[a.source].label}</Badge> },
    { key: "usd", header: t("adSpend.columns.amount") + " USD", align: "right", cell: (a) => <span className="mono font-bold">{formatUSD(a.amountUsd)}</span> },
    { key: "dzd", header: t("adSpend.columns.amount") + " DZD", align: "right", cell: (a) => <span className="mono text-text2">{formatDZD(a.amountDzd)}</span> },
    { key: "actions", header: t("common.actions"), align: "right", cell: (a) => <Button variant="ghost" size="xs" className="text-danger" onClick={() => setToDelete(a)}><Trash2 className="h-3 w-3" /></Button> },
  ];

  return (
    <PageContainer>
      <PageHeader
        title={t("adSpend.title")}
        subtitle={`${adSpend.length} entrées · ${formatUSD(stats.total)} total`}
        actions={<Button size="sm" onClick={() => setAddOpen(true)}><Plus className="h-3 w-3" /> {t("adSpend.add")}</Button>}
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <KPICard label="Total dépensé (USD)" value={formatUSD(stats.total)} />
        <KPICard label="Carte agence" value={<span className="text-violet">{formatUSD(stats.agency)}</span>} />
        <KPICard label="Carte client" value={<span className="text-info">{formatUSD(stats.client)}</span>} />
        <KPICard label="Équiv. DZD" value={formatDZD(usdToDzd(stats.total))} />
      </div>

      {isError && <ApiFallbackBanner onRetry={() => refetch()} />}

      <div className="mb-3.5">
        <SegmentedFilter options={FILTERS} value={filter} onChange={setFilter} />
      </div>

      <Card>
        {isLoading ? (
          <LoadingState rows={6} />
        ) : (
          <DataTable columns={columns} rows={visible} rowKey={(a) => a.id} empty={{ title: "Aucune dépense pub" }} />
        )}
      </Card>

      <AddAdSpendModal open={addOpen} onClose={() => setAddOpen(false)} onSave={onCreate} clients={clients} />

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(undefined)}
        title="Archiver la dépense"
        message={`Archiver la dépense ${toDelete ? formatUSD(toDelete.amountUsd) : ""} de ${toDelete?.clientName} ?`}
        confirmLabel="Archiver"
        onConfirm={onArchiveConfirm}
      />
    </PageContainer>
  );
}

function AddAdSpendModal({
  open,
  onClose,
  onSave,
  clients,
}: {
  open: boolean;
  onClose: () => void;
  onSave: (input: AdSpendInput) => void;
  clients: { id: string; name: string }[];
}) {
  const { PLATFORM, CARD_SOURCE } = useLabels();
  const [clientId, setClientId] = useState("");
  const [platform, setPlatform] = useState<Platform>("META");
  const [source, setSource] = useState<CardSource>("AGENCY_CARD");
  const [usd, setUsd] = useState("");
  const [error, setError] = useState("");

  const selectedId = clientId || clients[0]?.id || "";

  const submit = () => {
    const value = Number(usd);
    const client = clients.find((c) => c.id === selectedId);
    if (!client || !value || value <= 0) {
      setError("Montant invalide");
      return;
    }
    onSave({ clientId: client.id, clientName: client.name, platform, source, amountUsd: value });
    setUsd("");
    setError("");
  };

  return (
    <Modal open={open} onClose={onClose} title="Saisir une dépense publicitaire"
      footer={<><Button variant="ghost" size="sm" onClick={onClose}>Annuler</Button><Button size="sm" onClick={submit}>Ajouter</Button></>}>
      <Field label="Client" required>
        <select className={fieldInputClass} value={selectedId} onChange={(e) => setClientId(e.target.value)}>
          {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Plateforme" required>
          <select className={fieldInputClass} value={platform} onChange={(e) => setPlatform(e.target.value as Platform)}>
            {(Object.keys(PLATFORM) as Platform[]).map((p) => <option key={p} value={p}>{PLATFORM[p].label}</option>)}
          </select>
        </Field>
        <Field label="Source" required>
          <select className={fieldInputClass} value={source} onChange={(e) => setSource(e.target.value as CardSource)}>
            {(Object.keys(CARD_SOURCE) as CardSource[]).map((s) => <option key={s} value={s}>{CARD_SOURCE[s].label}</option>)}
          </select>
        </Field>
      </div>
      <Field label="Montant USD" required error={error}>
        <input type="number" className={fieldInputClass} value={usd} onChange={(e) => setUsd(e.target.value)} />
      </Field>
    </Modal>
  );
}
